import React, { useState, useRef } from 'react'
import { Camera, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { useToast } from '@/components/ui/use-toast'
import api from '@/lib/api'
import { updateUser } from '@/redux/userSlice'
import { useDispatch } from 'react-redux'
import { ImageCropModal } from '@/components/common/ImageCropModal'

interface AvatarUploadProps {
  currentAvatar?: string
  userName?: string
  onAvatarUpdate?: (newAvatarUrl: string) => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatar,
  userName,
  onAvatarUpdate,
  size = 'lg',
  className = '',
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string>('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { toast } = useToast()
  const dispatch = useDispatch()

  const sizeClasses = {
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32',
  }

  const buttonSizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  }

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file.',
        variant: 'destructive',
      })
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image smaller than 10MB.',
        variant: 'destructive',
      })
      return
    }

    // Create preview for cropper
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        setSelectedImage(e.target.result as string)
        setCropModalOpen(true)
      }
    }
    reader.readAsDataURL(file)

    // Reset input value to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCropComplete = async (croppedBlob: Blob) => {
    // Create preview of cropped image
    const url = URL.createObjectURL(croppedBlob)
    setPreviewUrl(url)

    // Convert blob to File for upload
    const file = new File([croppedBlob], 'avatar.webp', { type: 'image/webp' })

    handleUpload(file)
  }

  const handleUpload = async (file: File) => {
    setIsUploading(true)

    try {
      // Get presigned URL
      const uploadResponse = await api.post('/users/me/avatar-upload-url', {
        mimeType: file.type,
      })

      const { presignedUrl, publicUrl } = uploadResponse.data

      // Upload file to S3
      const s3Response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-amz-acl': 'public-read',
        },
      })

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.statusText}`)
      }

      // Call the update callback or update directly
      if (onAvatarUpdate) {
        await onAvatarUpdate(publicUrl)
      } else {
        // Update user profile via API
        await api.patch('/users/me/avatar', { avatarUrl: publicUrl })

        // Update Redux state
        dispatch(updateUser({ avatar: publicUrl }))

        toast({
          title: 'Avatar updated',
          description: 'Your profile picture has been updated successfully.',
        })
      }
    } catch (error) {
      console.error('Avatar upload error:', error)
      toast({
        title: 'Upload failed',
        description: 'Failed to update your profile picture. Please try again.',
        variant: 'destructive',
      })
      setPreviewUrl(null)
    } finally {
      setIsUploading(false)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const displayAvatar = previewUrl || currentAvatar

  return (
    <div className={`relative inline-block ${className}`}>
      <Avatar className={`${sizeClasses[size]} border-2 border-foreground/20`}>
        <AvatarImage
          src={displayAvatar}
          alt={userName || 'Profile picture'}
          className="object-cover"
        />
        <AvatarFallback className="bg-muted text-muted-foreground text-lg font-medium">
          {userName?.charAt(0)?.toUpperCase() || 'U'}
        </AvatarFallback>
      </Avatar>

      {/* Upload button overlay */}
      <Button
        type="button"
        onClick={handleButtonClick}
        disabled={isUploading}
        className={`absolute -bottom-1 -right-1 ${buttonSizeClasses[size]} rounded-full bg-primary hover:bg-primary/90 text-primary-foreground p-0 border-2 border-foreground shadow-lg`}
      >
        {isUploading ? (
          <Loader2 className={`${iconSizeClasses[size]} animate-spin`} />
        ) : (
          <Camera className={iconSizeClasses[size]} />
        )}
      </Button>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Loading overlay */}
      {isUploading && (
        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
          <div className="text-foreground text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-1" />
          </div>
        </div>
      )}

      <ImageCropModal
        open={cropModalOpen}
        imageUrl={selectedImage}
        onClose={() => setCropModalOpen(false)}
        onCropComplete={handleCropComplete}
        title="Crop Profile Picture"
        cropShape="rect"
      />
    </div>
  )
}

export default AvatarUpload
